import { DropdownMenu as KobalteMenu } from '@kobalte/core/dropdown-menu'
import { IconCheck, IconChevronDown } from '@tabler/icons-solidjs'
import { For, type JSX, Show, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'

export interface ActionMenuItem {
  label: string
  icon?: JSX.Element
  /** Consecutive items sharing a group render together under one heading, separated by a rule. */
  group?: string
  /**
   * Marks the item as the current choice. Present means the menu is picking one of several
   * things, so the item is announced as a radio rather than as a command.
   */
  selected?: boolean
  disabled?: boolean
  onSelect: () => void
}

export interface ActionMenuProps {
  /** Accessible name. On `button` shape it must contain the visible text (WCAG 2.5.3). */
  label: string
  items: readonly ActionMenuItem[]
  children: JSX.Element
  class?: string
  /**
   * `icon` is a square control carrying `label` as its tooltip. `button` sizes to its content
   * and appends a chevron — a control that opens a menu has to say that it does.
   */
  shape?: 'icon' | 'button'
  placement?: 'bottom-start' | 'bottom-end'
  disabled?: boolean
}

interface ActionMenuGroup {
  label?: string
  items: ActionMenuItem[]
}

/** Accessible overflow and picker menus for compact toolbars. */
export const ActionMenu = (props: ActionMenuProps) => {
  const [local] = splitProps(props, [
    'label',
    'items',
    'children',
    'class',
    'shape',
    'placement',
    'disabled',
  ])
  const shape = () => local.shape ?? 'icon'
  let trigger: HTMLButtonElement | undefined

  const groups = (): ActionMenuGroup[] =>
    local.items.reduce<ActionMenuGroup[]>((result, item) => {
      const last = result.at(-1)
      if (last && last.label === item.group) last.items.push(item)
      else result.push({ label: item.group, items: [item] })
      return result
    }, [])

  return (
    <KobalteMenu placement={local.placement ?? 'bottom-end'} gutter={4} modal={false}>
      <KobalteMenu.Trigger
        ref={trigger}
        class={cn(
          'inline-flex shrink-0 items-center justify-center gap-control-gap rounded-action',
          'border border-border-strong bg-surface text-body text-text shadow-raise',
          'transition-[background-color,color,border-color,transform] duration-(--duration-press) ease-out',
          'hover:bg-surface-sunken active:scale-[0.98]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          'disabled:pointer-events-none disabled:opacity-45',
          '[&_svg]:size-3.5 [&_svg]:shrink-0',
          shape() === 'icon'
            ? 'size-(--size-control-sm)'
            : 'h-(--size-control-sm) max-w-56 px-control-x-sm font-medium',
          local.class,
        )}
        aria-label={local.label}
        title={shape() === 'icon' ? local.label : undefined}
        disabled={local.disabled}
      >
        {local.children}
        <Show when={shape() === 'button'}>
          <IconChevronDown strokeWidth={1.5} class="text-text-subtle" aria-hidden="true" />
        </Show>
      </KobalteMenu.Trigger>
      <KobalteMenu.Portal
        mount={trigger?.closest<HTMLElement>('aside[role="dialog"]') ?? undefined}
      >
        <KobalteMenu.Content class="z-50 max-h-[min(24rem,var(--kb-popper-content-available-height))] min-w-56 overflow-y-auto rounded-card border border-border bg-surface p-1.5 text-body text-text shadow-overlay outline-none">
          <For each={groups()}>
            {(group, index) => (
              <>
                <Show when={index() > 0}>
                  <KobalteMenu.Separator class="my-1.5 h-px border-0 bg-border" />
                </Show>
                <KobalteMenu.Group>
                  <Show when={group.label}>
                    {(groupLabel) => (
                      <KobalteMenu.GroupLabel class="px-2.5 pt-0.5 pb-1 text-micro font-semibold text-text-subtle uppercase">
                        {groupLabel()}
                      </KobalteMenu.GroupLabel>
                    )}
                  </Show>
                  <For each={group.items}>
                    {(item) => (
                      <KobalteMenu.Item
                        class="flex cursor-default items-center gap-2.5 rounded-control px-2.5 py-2 outline-none transition-colors duration-(--duration-press) ease-out data-[disabled]:opacity-45 data-[highlighted]:bg-surface-sunken"
                        role={item.selected === undefined ? undefined : 'menuitemradio'}
                        aria-checked={item.selected}
                        disabled={item.disabled}
                        onSelect={item.onSelect}
                      >
                        <span class="grid size-4 shrink-0 place-items-center text-text-subtle [&_svg]:size-4">
                          <Show when={item.selected} fallback={item.icon}>
                            <IconCheck strokeWidth={2} aria-hidden="true" />
                          </Show>
                        </span>
                        <span class="min-w-0 flex-1 truncate">{item.label}</span>
                      </KobalteMenu.Item>
                    )}
                  </For>
                </KobalteMenu.Group>
              </>
            )}
          </For>
        </KobalteMenu.Content>
      </KobalteMenu.Portal>
    </KobalteMenu>
  )
}
